import { useState, useEffect, useMemo } from "react";
import { apiCall } from "../api";
import { Modal } from "./common/Modal";

const FREQ_OPTIONS = ["6 MONTH", "1 YEAR"];

// A six-month sheet is MNT-F-02 and a yearly one MNT-F-12. New templates start with the
// right one rather than blank, which is how 74 of them came to show "Doc No: —" on the
// sheet the fitter fills in. The letter some carry after it names a machine family and is
// added by hand.
const DOC_NO_BY_FREQUENCY = { "6 MONTH": "MNT-F-02", "1 YEAR": "MNT-F-12" };

const EMPTY_FORM = { id: null, machineType: "", frequency: "6 MONTH",
                     docNo: DOC_NO_BY_FREQUENCY["6 MONTH"], checkpoints: [] };

export default function PMCheckSheetsAdmin({ machines = [] }) {
  const machineTypeOptions = Array.from(new Set((machines || []).map(m => (m.name || "").toUpperCase()).filter(Boolean))).sort();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // "add" | "edit" | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const data = await apiCall("GET", "/pm-check-sheet-templates");
      setTemplates(data || []);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message || "Failed to load"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  // Every sheet carries a number of its own — MNT-F-02.1, .2, and so on — so a new one
  // takes the next free serial for its frequency rather than repeating the last.
  function nextDocNo(frequency, list) {
    const base = DOC_NO_BY_FREQUENCY[frequency];
    if (!base) return "";
    const used = new Set((list || [])
      .map(t => String(t.docNo || "").trim())
      .filter(d => d.startsWith(`${base}.`))
      .map(d => parseInt(d.slice(base.length + 1), 10))
      .filter(n => Number.isInteger(n)));
    let n = 1;
    while (used.has(n)) n += 1;
    return `${base}.${n}`;
  }

  function openAdd() {
    setForm({ ...EMPTY_FORM, checkpoints: [],
              docNo: nextDocNo(EMPTY_FORM.frequency, templates) });
    setErr("");
    setModalMode("add");
  }

  function openEdit(tpl) {
    setForm({
      id: tpl.id,
      machineType: tpl.machineType || "",
      frequency: tpl.frequency || "6 MONTH",
      docNo: tpl.docNo || "",
      checkpoints: (tpl.checkpoints || []).map(cp => ({
        text: cp.text || "",
        specification: cp.specification || "",
        checkingMethod: cp.checkingMethod || "",
      })),
    });
    setErr("");
    setModalMode("edit");
  }

  async function handleDelete(tpl) {
    if (!window.confirm(`DELETE template for ${tpl.machineType} (${tpl.frequency})?\n\nThis cannot be undone.`)) return;
    try {
      await apiCall("DELETE", `/pm-check-sheet-templates/${tpl.id}`);
      await load();
    } catch (e) { alert("Delete failed: " + (e?.response?.data?.detail || e?.message || "Unknown")); }
  }

  function addCheckpoint() {
    setForm(f => ({ ...f, checkpoints: [...f.checkpoints, { text: "", specification: "", checkingMethod: "" }] }));
  }

  function updateCheckpoint(idx, field, value) {
    setForm(f => ({
      ...f,
      checkpoints: f.checkpoints.map((cp, i) => i === idx ? { ...cp, [field]: value } : cp),
    }));
  }

  function removeCheckpoint(idx) {
    setForm(f => ({ ...f, checkpoints: f.checkpoints.filter((_, i) => i !== idx) }));
  }

  function moveCheckpoint(idx, dir) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= form.checkpoints.length) return;
    setForm(f => {
      const arr = [...f.checkpoints];
      const tmp = arr[idx]; arr[idx] = arr[newIdx]; arr[newIdx] = tmp;
      return { ...f, checkpoints: arr };
    });
  }

  async function handleSave() {
    setErr("");
    if (!form.machineType.trim()) { setErr("Machine Type is required"); return; }
    if (!form.frequency) { setErr("Frequency is required"); return; }
    if (form.checkpoints.length === 0) { setErr("At least one checkpoint is required"); return; }
    if (form.checkpoints.some(cp => !cp.text.trim())) { setErr("All checkpoints must have a name"); return; }
    setSaving(true);
    try {
      const payload = {
        machineType: form.machineType.trim().toUpperCase(),
        frequency: form.frequency,
        docNo: form.docNo.trim(),
        checkpoints: form.checkpoints.map(cp => ({
          text: cp.text.trim(),
          specification: (cp.specification || "").trim(),
          checkingMethod: (cp.checkingMethod || "").trim(),
        })),
      };
      if (modalMode === "edit") {
        await apiCall("PUT", `/pm-check-sheet-templates/${form.id}`, payload);
      } else {
        await apiCall("POST", "/pm-check-sheet-templates", payload);
      }
      setModalMode(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setSaving(false); }
  }

  // 82 templates in two long tables is a lot of scrolling to reach one machine type.
  // Search matches the type and the document number, so "MNT-F-02.26" finds its sheet
  // just as well as "MECHANICAL PRESS".
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [freqFilter, setFreqFilter] = useState("ALL");
  const [collapsed, setCollapsed] = useState({});

  // Every machine type that has a sheet, each with how many it has — pick one straight
  // off the list instead of having to know how it is spelled.
  const typeOptions = useMemo(() => {
    const counts = new Map();
    for (const t of templates) {
      const name = String(t.machineType || "").trim();
      if (name) counts.set(name, (counts.get(name) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([machineType, count]) => ({ machineType, count }))
      .sort((a, b) => a.machineType.localeCompare(b.machineType));
  }, [templates]);

  const matching = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => {
      if (typeFilter !== "ALL" && String(t.machineType || "").trim() !== typeFilter) return false;
      if (!q) return true;
      return String(t.machineType || "").toLowerCase().includes(q)
        || String(t.docNo || "").toLowerCase().includes(q);
    });
  }, [templates, search, typeFilter]);

  const grouped = {
    "6 MONTH": matching.filter(t => t.frequency === "6 MONTH"),
    "1 YEAR": matching.filter(t => t.frequency === "1 YEAR"),
  };
  const shownFreqs = FREQ_OPTIONS.filter(f => freqFilter === "ALL" || f === freqFilter);
  const totalShown = shownFreqs.reduce((n, f) => n + grouped[f].length, 0);

  return (
    <div className="space-y-4" data-testid="pm-check-sheets-admin">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800">PM Check Sheet Templates</h3>
          <p className="text-xs text-gray-500">Templates used during scheduled PM completion. Mapped to machines by Machine Type.</p>
        </div>
        <button onClick={openAdd} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-blue-700" data-testid="add-pm-template-btn">+ Add Template</button>
      </div>

      {!loading && (
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search machine type or document no..."
            className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="pm-template-search" />
          {/* Long names are shown in full rather than cut off — a browser sizes a select
              to its widest option, so "LEAKAGE TESTING MACHINE" is readable without
              squeezing the search box. */}
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white max-w-full"
            data-testid="pm-template-type-filter" title="Jump to one machine type">
            <option value="ALL">All machine types ({typeOptions.length})</option>
            {typeOptions.map(o => (
              <option key={o.machineType} value={o.machineType}>
                {o.machineType} ({o.count})
              </option>
            ))}
          </select>
          <select value={freqFilter} onChange={e => setFreqFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="pm-template-freq-filter">
            <option value="ALL">All frequencies</option>
            {FREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          {(search || freqFilter !== "ALL" || typeFilter !== "ALL") && (
            <button onClick={() => { setSearch(""); setFreqFilter("ALL"); setTypeFilter("ALL"); }}
              className="border border-red-300 text-red-500 px-3 py-2 rounded-lg text-sm hover:bg-red-50"
              data-testid="pm-template-clear">Clear</button>
          )}
          <span className="text-xs text-gray-500 whitespace-nowrap" data-testid="pm-template-count">
            {totalShown} of {templates.length} templates
          </span>
        </div>
      )}

      {loading && <div className="text-sm text-gray-400">Loading...</div>}
      {err && !modalMode && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      {!loading && totalShown === 0 && (
        <div className="bg-white rounded-xl border px-4 py-6 text-center text-sm text-gray-400"
          data-testid="pm-template-no-match">
          No template matches that search.
        </div>
      )}

      {!loading && shownFreqs.map(freq => (
        grouped[freq].length === 0 && (search || typeFilter !== "ALL") ? null : (
        <div key={freq} className="bg-white rounded-xl border overflow-hidden">
          {/* Each frequency folds away, so the one being worked on can be read without
              the other 40 rows underneath it. */}
          <button
            onClick={() => setCollapsed(c => ({ ...c, [freq]: !c[freq] }))}
            className="w-full bg-gray-50 px-4 py-2 border-b text-sm font-semibold text-gray-700 flex items-center gap-2 hover:bg-gray-100"
            data-testid={`pm-template-group-${freq.replace(/\s+/g, "-")}`}>
            <span>{freq}</span>
            <span className="text-xs font-normal text-gray-500">({grouped[freq].length})</span>
            <span className="ml-auto text-gray-400 text-xs">{collapsed[freq] ? "\u25BC" : "\u25B2"}</span>
          </button>
          {!collapsed[freq] && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Machine Type</th>
                <th className="px-3 py-2 text-left">Doc No</th>
                <th className="px-3 py-2 text-left">Checkpoints</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grouped[freq].length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400 text-xs">{(search || typeFilter !== "ALL") ? "No match in this frequency." : "No templates for this frequency."}</td></tr>
              ) : grouped[freq].map(tpl => (
                <tr key={tpl.id} className="border-t hover:bg-blue-50">
                  <td className="px-3 py-2 font-medium text-gray-800 break-words whitespace-normal">{tpl.machineType}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{tpl.docNo || "\u2014"}</td>
                  <td className="px-3 py-2 text-gray-600">{(tpl.checkpoints || []).length}</td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button onClick={() => openEdit(tpl)} className="text-blue-600 text-xs hover:underline">Edit</button>
                    <button onClick={() => handleDelete(tpl)} className="text-red-600 text-xs hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
        )
      ))}

      {modalMode && (
        <Modal title={modalMode === "edit" ? `Edit Template \u2014 ${form.machineType}` : "Add PM Check Sheet Template"} onClose={() => setModalMode(null)}>
          <div className="space-y-3 p-3 max-h-[80vh] overflow-y-auto">
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Machine Type *</label>
                <input value={form.machineType} onChange={e => setForm(f => ({ ...f, machineType: e.target.value }))}
                  list="pm-template-machine-types"
                  placeholder="Pick or type \u2014 e.g. PNEUMATIC PRESS"
                  className="w-full px-3 py-2 rounded border border-gray-300 text-sm" />
                <datalist id="pm-template-machine-types">
                  {machineTypeOptions.map(t => <option key={t} value={t} />)}
                </datalist>
                <p className="text-xs text-gray-400 mt-1">Pick from existing machine types or type a new one. Saved value will match machine.name (case-insensitive).</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Frequency *</label>
                {/* Changing the frequency moves the sheet to the other document number,
                    but only while it is still the one this editor suggested — a number
                    typed or edited by hand is never overwritten. */}
                <select value={form.frequency} onChange={e => setForm(f => ({
                  ...f, frequency: e.target.value,
                  // Only while the number is still one this editor suggested. A number
                  // typed or edited by hand is never moved.
                  docNo: /^(MNT-F-02|MNT-F-12)(\.\d+)?$/.test((f.docNo || "").trim())
                    ? nextDocNo(e.target.value, templates) || f.docNo : f.docNo,
                }))}
                  className="w-full px-3 py-2 rounded border border-gray-300 text-sm bg-white">
                  {FREQ_OPTIONS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Doc No</label>
                <input value={form.docNo} onChange={e => setForm(f => ({ ...f, docNo: e.target.value }))}
                  placeholder="e.g. MNT-F-02 B"
                  className="w-full px-3 py-2 rounded border border-gray-300 text-sm" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500 font-semibold">Checkpoints *</label>
                <button onClick={addCheckpoint} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">+ Add Checkpoint</button>
              </div>
              <table className="w-full text-xs border">
                <thead className="bg-gray-100 text-gray-600">
                  <tr>
                    <th className="border px-1 py-1 w-8">#</th>
                    <th className="border px-1 py-1 text-left">Checkpoint Name *</th>
                    <th className="border px-1 py-1 text-left">Specification</th>
                    <th className="border px-1 py-1 text-left">Checking Method</th>
                    <th className="border px-1 py-1 w-16 text-center">Order</th>
                    <th className="border px-1 py-1 w-12 text-center">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {form.checkpoints.length === 0 ? (
                    <tr><td colSpan={6} className="border px-2 py-3 text-center text-gray-400">No checkpoints. Click "+ Add Checkpoint" to add one.</td></tr>
                  ) : form.checkpoints.map((cp, i) => (
                    <tr key={i} className="border-b">
                      <td className="border px-1 py-1 text-center text-gray-500">{i + 1}</td>
                      <td className="border px-1 py-1"><input value={cp.text} onChange={e => updateCheckpoint(i, "text", e.target.value)} className="w-full px-1 py-0.5 border rounded text-xs" /></td>
                      <td className="border px-1 py-1"><input value={cp.specification} onChange={e => updateCheckpoint(i, "specification", e.target.value)} placeholder="e.g. 5\u20137 bar" className="w-full px-1 py-0.5 border rounded text-xs" /></td>
                      <td className="border px-1 py-1"><input value={cp.checkingMethod} onChange={e => updateCheckpoint(i, "checkingMethod", e.target.value)} placeholder="e.g. Visual & Manual" className="w-full px-1 py-0.5 border rounded text-xs" /></td>
                      <td className="border px-1 py-1 text-center space-x-1">
                        <button onClick={() => moveCheckpoint(i, -1)} className="text-blue-500 hover:underline">{"\u2191"}</button>
                        <button onClick={() => moveCheckpoint(i, 1)} className="text-blue-500 hover:underline">{"\u2193"}</button>
                      </td>
                      <td className="border px-1 py-1 text-center">
                        <button onClick={() => removeCheckpoint(i)} className="text-red-500 hover:underline">{"\u2715"}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={handleSave} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-blue-700 disabled:opacity-50" data-testid="save-pm-template-btn">{saving ? "Saving..." : (modalMode === "edit" ? "Update Template" : "Add Template")}</button>
              <button onClick={() => setModalMode(null)} className="border border-gray-300 text-gray-600 px-4 py-2 rounded text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
